<dashboard-loader *ngIf="loadingModule$ | async" [title]="'Loading module details...'"></dashboard-loader>
<div *ngIf="!isEdit || (moduleLoaded$ | async)" class="dashboard-module-form">
  <div class="dashboard-module-form__name" *ngIf="isEdit">
    {{ selectedModule?.translations | dashboardTranslate: 'title':selectedLanguage?.code }}
  </div>
  <form
    class="dashboard-module-form__form"
    [formGroup]="moduleForm"
    dashboardConnectForm
    [connectForm]="isEdit ? selectedModule : null"
    fxLayout="column"
    fxLayoutGap="30px"
    fxLayoutAlign="center"
  >
    <mat-form-field fxFill>
      <input matInput formControlName="name" placeholder="Unique name (researcher only)" />
      <mat-error *ngIf="name.hasError('required')"> Module unique name is <strong>required</strong></mat-error>
    </mat-form-field>
    <div class="identifier" *ngIf="selectedModule">
      Unique module identifier (for researcher only in download file)
      <span>{{ esmModule ? 'ESM-' : 'M-' }}{{ selectedModule?.id }}</span>
    </div>
    <div>
      <mat-tab-group
        mat-align-tabs="end"
        (selectedTabChange)="selectTemplateLanguage($event)"
        #languageTabs
        [disablePagination]="true"
        [selectedIndex]="selectedLanguage?.tabIndex"
        (animationDone)="selectFirstTab()"
      >
        <mat-tab *ngFor="let language of supportedLanguages" [label]="language?.name">
          <ng-container *ngTemplateOutlet="tabContent"></ng-container>
        </mat-tab>
      </mat-tab-group>
      <ng-template #tabContent>
        <div formArrayName="translations" *ngFor="let item of translations.controls; let i = index">
          <mat-form-field
            [formGroupName]="i"
            *ngIf="item.get('language').value === selectedLanguage?.code && item.get('identifier').value !== 'reminder'"
            fxFlex="100"
          >
            <input
              matInput
              formControlName="translation"
              [placeholder]="
                item.get('identifier').value === 'title'
                  ? 'Module title'
                  : item.get('identifier').value === 'prefix'
                  ? 'Prefix - ex. I am a../ I want to..'
                  : item.get('identifier').value === 'description'
                  ? 'Short description'
                  : ''
              "
            />
            <mat-error *ngIf="item.get('identifier').value === 'title' && item.get('translation').hasError('required')">
              Module title is <strong>required</strong>
            </mat-error>
          </mat-form-field>
        </div>
      </ng-template>
    </div>
    <div *ngIf="selectedStudy?.type === StudyType.TIIM">
      <dashboard-image-upload
        formControlName="imagePath"
        [uuid]="imageUploadUuid"
        [label]="'Image'"
        [isEdit]="isEdit"
        [isImageToEdit]="isImageToEdit"
        [imgPath]="imgPath"
        [keyForUpload]="keyForUpload"
        [idsForUpload]="idsForUpload"
      >
      </dashboard-image-upload>
    </div>

    <div *ngIf="selectedStudy?.type === StudyType.TIIM" fxLayout="column" fxLayoutGap="30px">
      <div fxLayout="column" fxLayoutGap="10px">
        <mat-form-field appearance="fill">
          <mat-label>Question Order</mat-label>
          <mat-select formControlName="randomization">
            <mat-option [value]="RANDOMIZATION_VALUES.ROUTING">Specific Order with Routing</mat-option>
            <mat-option [value]="RANDOMIZATION_VALUES.MODULE">Randomization per Module</mat-option>
            <mat-option [value]="RANDOMIZATION_VALUES.CATEGORY">Randomization per Category</mat-option>
          </mat-select>
        </mat-form-field>

        <div *ngIf="randomization.value === RANDOMIZATION_VALUES.CATEGORY">
          <button mat-button color="primary" (click)="openCategoryDialog()">
            {{ 'Configure Categories' | uppercase }}
          </button>
        </div>
      </div>

      <div fxLayout="column" fxLayoutGap="10px">
        <div class="dashboard-module-form__form-label">Progress bar configuration</div>
        <mat-checkbox formControlName="progressBar">Add progress bar</mat-checkbox>
      </div>
    </div>
    <div *ngIf="selectedStudy?.healthEnabled || selectedStudy?.phenotypingEnabled" fxLayout="column" fxLayoutGap="20px">
      <div class="dashboard-module-form__form-title">Sensor configuration</div>
      <div class="dashboard-module-form__form-description">
        Choose what type of data should be collected in which period of time.
      </div>
      <div
        class="dashboard-module-form__form-sensor"
        *ngIf="selectedStudy?.healthEnabled"
        fxLayout="row"
        fxLayoutAlign="space-between center"
      >
        <div fxLayout="column" fxLayoutGap="10px">
          <div class="dashboard-module-form__form-label">Sensor types</div>
          <mat-checkbox formControlName="heartRateType">Heart rate</mat-checkbox>
          <mat-checkbox formControlName="stepsType">Steps</mat-checkbox>
          <mat-checkbox formControlName="sleepType" (change)="updateSleepChecked()">Sleep</mat-checkbox>
          <div *ngIf="sleepChecked" class="dashboard-module-form__form-label">
            Note: iOS phones can only share sleep and awake data, whereas Android phones can also share the type of
            sleep (light, deep or rem).
          </div>
        </div>
        <div fxLayout="column" fxLayoutGap="20px">
          <div class="dashboard-module-form__form-label">Time period</div>
          <mat-form-field appearance="fill">
            <mat-select
              matTooltip="Note: Selecting week or month can lead to slow upload and download of health data!"
              matTooltipPosition="above"
              formControlName="sensorDataTimePeriod"
            >
              <mat-option *ngFor="let period of SENSOR_PERIODS" [value]="period.value">{{ period.label }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </div>
      <div
        class="dashboard-module-form__form-sensor"
        *ngIf="selectedStudy?.phenotypingEnabled"
        fxLayout="row"
        fxLayoutAlign="space-between center"
      >
        <div fxLayout="column" fxLayoutGap="10px">
          <div class="dashboard-module-form__form-label">Choose digital footprint and GPS data collection</div>
          <mat-checkbox formControlName="batteryLifeType">Battery life</mat-checkbox>
          <mat-checkbox formControlName="locationType">GPS</mat-checkbox>
        </div>
      </div>
      <div fxLayout="row" fxLayoutGap="30px" *ngIf="sensorDataTimePeriod?.value === 0">
        <mat-form-field>
          <input
            matInput
            [ngxMatDatetimePicker]="dateStartsPicker"
            placeholder="Date starts at"
            formControlName="sensorDataStart"
            [min]="selectedStudy?.startsAt"
            [max]="selectedStudy?.endsAt"
          />
          <mat-datepicker-toggle matSuffix [for]="dateStartsPicker"></mat-datepicker-toggle>
          <ngx-mat-datetime-picker #dateStartsPicker [showSeconds]="false" [stepHour]="1" [stepMinute]="1">
          </ngx-mat-datetime-picker>
          <mat-error *ngIf="sensorDataStart.hasError('required')"> Starts at is <strong>required</strong></mat-error>
        </mat-form-field>
        <mat-form-field>
          <input
            matInput
            [ngxMatDatetimePicker]="dateEndsPicker"
            placeholder="Date ends at"
            formControlName="sensorDataEnd"
            [min]="selectedStudy?.startsAt"
            [max]="selectedStudy?.endsAt"
          />
          <mat-datepicker-toggle [disabled]="false" matSuffix [for]="dateEndsPicker"></mat-datepicker-toggle>
          <ngx-mat-datetime-picker #dateEndsPicker [showSeconds]="false" [stepHour]="1" [stepMinute]="1">
          </ngx-mat-datetime-picker>
          <mat-error *ngIf="sensorDataEnd.hasError('required')"> Ends at is <strong>required</strong></mat-error>
        </mat-form-field>
      </div>
    </div>
  </form>
  <div *ngIf="isEdit" fxLayout="column" fxLayoutAlign="space-between start" fxLayoutGap="15px">
    <div fxLayout="column" fxLayoutAlign="space-between start" fxLayoutGap="10px">
      <div class="dashboard-module-form__form-title">Import module items</div>
      <div class="dashboard-module-form__form-description">
        This is where you can import module items into this module.
      </div>
      <div class="dashboard-module-form__description">Click on the button to import module items from this module.</div>
      <div *ngIf="privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.MODULES, PRIVILEGE_ACTIONS.ALL)">
        <div fxLayout="row" fxLayoutGap="20px">
          <input
            class="dashboard-module-form-file-input"
            #fileInput
            (change)="fileChanged($event)"
            onclick="this.value = null"
            type="file"
            accept=".csv"
          />
          <button mat-raised-button color="accent" (click)="fileInput.click()">
            {{ SELECT_CSV_BUTTON_LABEL | uppercase }}
          </button>
          <button
            mat-raised-button
            color="accent"
            *ngIf="filesToUpload && filesToUpload.length"
            class="btn"
            (click)="importModuleItems()"
          >
            {{ 'Import' | uppercase }}
          </button>
        </div>
        <dashboard-loader
          *ngIf="importingModuleItems$ | async"
          [title]="'Importing Module Items...'"
        ></dashboard-loader>
      </div>
    </div>
    <div fxLayout="column" fxLayoutAlign="space-between start" fxLayoutGap="10px">
      <div class="dashboard-module-form__form-title">Sample files</div>
      <div class="dashboard-module-form__description">
        Below are some sample CSV files which you can use as a basis for your import.
      </div>
      <a mat-button color="primary" href="/assets/import/import-all-types.csv" download>
        {{ 'CSV Containing all item types' | uppercase }}
      </a>
      <a
        *ngIf="selectedStudy?.type === StudyType.CC"
        mat-button
        color="primary"
        href="/assets/import/import-careercompass.csv"
        download
      >
        {{ 'CSV with Career Compass Items' | uppercase }}
      </a>
    </div>
    <div fxLayout="column" fxLayoutAlign="space-between start" fxLayoutGap="10px">
      <div class="dashboard-module-form__form-title">Export module items</div>
      <button mat-button color="primary" (click)="doExportCall()">{{ 'Export to CSV' | uppercase }}</button>
    </div>
  </div>
  <div fxLayout="column" fxLayoutAlign="space-around end" fxFill>
    <button
      mat-raised-button
      color="accent"
      *ngIf="privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.MODULES, PRIVILEGE_ACTIONS.ALL)"
      (click)="saveModule()"
    >
      {{ 'Save Module' | uppercase }}
    </button>
  </div>
</div>
